import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Translate } from 'react-localize-redux';
import IconMobile from 'resources/svg/icon_mobile.svg';
import Highlighter from 'react-highlight-words';
import Icon, { IconSizes } from '../../../components/foundations/Icon';
import OverflowingText from '../../../components/foundations/OverflowingText';
import SearchTextField from '../../../components/molecules/SearchTextField/SearchTextField';
import { FauxButton } from '../../../components/widgets';
import { translationPrefix } from '../../utils';
import css from './CollapsibleItemGrid.css';

const NoSearchResults = () => (
    <div className={css.noResults}>
        <Translate id={`${translationPrefix}.EMPTY_STATE.NO_SEARCH_RESULTS`} />
    </div>
);

export const ITEMS_TO_SHOW = 12;

const IconItem = ({ icon, label }) => (
    <div className={css.iconItemContainer}>
        {icon ? <img src={icon} alt="" className={css.iconItemIcon} /> : <IconMobile className={css.fallbackIcon} />}
        <OverflowingText className={css.iconItemLabel}>{label}</OverflowingText>
    </div>
);

IconItem.propTypes = {
    icon: PropTypes.string,
    label: PropTypes.node,
};

const DefaultItem = ({ item, search }) => {
    const label = <Highlighter searchWords={[search]} textToHighlight={item.displayName || item.name} autoEscape />;
    return <IconItem icon={item.icon} label={label} />;
};

DefaultItem.propTypes = {
    search: PropTypes.string,
    item: PropTypes.shape({
        icon: PropTypes.string,
        displayName: PropTypes.string,
        name: PropTypes.string,
    }),
};

const CollapsibleItemGrid = ({
    items,
    totalCount,
    renderItem: RenderItem = DefaultItem,
    renderContent,
    contentClassName,
}) => {
    const containerRef = useRef(null);
    const searchContainerRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (expanded) {
            searchContainerRef.current?.querySelector('input')?.focus({ preventScroll: true });
        }
    }, [expanded]);

    const itemCount = totalCount ?? (items ? items.length : 0);
    const hasMore = itemCount > ITEMS_TO_SHOW;
    const contentHolderClassName = css.groupContentHolder;

    const expand = () => {
        setExpanded(true);
        const stepCard = containerRef.current?.closest('[class*="card"]');
        (stepCard || containerRef.current)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const collapse = () => {
        setExpanded(false);
        setSearch('');
    };

    const defaultContent = () => {
        const visibleItems = expanded
            ? items.filter(item => !search || (item.displayName || '').toLowerCase().includes(search.toLowerCase()))
            : items.slice(0, ITEMS_TO_SHOW);

        if (expanded && search && visibleItems.length === 0) {
            return <NoSearchResults />;
        }

        return (
            <div className={contentHolderClassName}>
                {visibleItems.map(item => (
                    <RenderItem key={item.id || item.displayName || item.name} item={item} search={search} />
                ))}
            </div>
        );
    };

    const renderBody = () => {
        if (renderContent) {
            const content = renderContent({ expanded, search, contentHolderClassName });
            const hasResults = React.Children.toArray(content).some(child => child != null);
            if (expanded && search && !hasResults) {
                return <NoSearchResults />;
            }
            return content;
        }
        return defaultContent();
    };

    return (
        <div ref={containerRef} className={css.container}>
            {expanded && hasMore && (
                <div ref={searchContainerRef} className={css.searchContainer}>
                    <SearchTextField search={search} onSearchChange={setSearch} clearable className={css.searchInput} />
                </div>
            )}
            <div className={contentClassName}>{renderBody()}</div>
            {hasMore && (
                <FauxButton className={css.showMoreButton} onClick={expanded ? collapse : expand}>
                    <Icon
                        name={expanded ? 'chevron-up' : 'chevron-down'}
                        size={IconSizes.X_SMALL}
                        className={css.arrowIcon}
                    />
                    <Translate id={`${translationPrefix}.REVIEW.${expanded ? 'SHOW_LESS' : 'VIEW_ALL'}`} />
                </FauxButton>
            )}
        </div>
    );
};

CollapsibleItemGrid.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            displayName: PropTypes.string,
            icon: PropTypes.string,
        })
    ),
    totalCount: PropTypes.number,
    renderItem: PropTypes.elementType,
    renderContent: PropTypes.func,
    contentClassName: PropTypes.string,
};

export default CollapsibleItemGrid;
